import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import SPMActivityDialog, {
  type ActivityData
} from 'Components/SPMActivityDialog/SPMActivityDialog'
import { WithLoader } from 'Components/WithLoader'
import ServiceProviderDrawer from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderDrawer'
import ServiceProviderList from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderList'
import { useSingleServiceProvider } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks'
import useServiceProvidersWithParams from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import {
  fetchServiceProviders,
  fetchSingleServiceProvider,
  type ServiceProvider,
  ServiceProviderType,
  updateSingleServiceProvider
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import {
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabList } from '@mui/lab'
import TabContext from '@mui/lab/TabContext'
import { Box, Tab, Typography } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const TAB_PARAM_KEY = 'tab'
export const SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY = 'serviceProviderDetailsDrawerTab'

export default function ServiceProviderListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers`
  const user = useTypedSelector(state => state.user.user)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {
    serviceProviders,
    loading,
    total_results,
    params,
    setParams,
    selectedServiceProviderId,
    setSelectedServiceProviderId,
    fetchServiceProvidersWithParams
  } = useServiceProvidersWithParams()
  const [searchText, setSearchText] = useState(params.name ?? '')
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? ''
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)

  const { singleServiceProvider, deleteServiceProvider, deleteLoading, submitLoading } =
    useSingleServiceProvider(undefined)

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedServiceProviderId)
  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Service providers`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleDrawerClose = () => {
    setSelectedServiceProviderId('')
    setIsDrawerOpen(false)
    searchParams.delete(SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string) => {
    setSelectedServiceProviderId(id)
    setIsDrawerOpen(true)
  }

  const handleActivityDialogOpen = () => {
    setActivityDialogOpen(true)
  }

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false)
  }

  const walttiServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    },
    {
      label: t`Ticket machine`,
      value: ServiceProviderType.TICKET_MACHINE
    },
    {
      label: t`Webshop`,
      value: ServiceProviderType.WEBSHOP
    },
    {
      label: t`Mobile`,
      value: ServiceProviderType.MOBILE
    },
    {
      label: t`Event ticket`,
      value: ServiceProviderType.EVENT_TICKET
    }
  ]

  const hslServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`HSL digital channel`,
      value: ServiceProviderType.HSL_DIGITAL_CHANNEL
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    }
  ]

  const serviceProviderTypes = user?.is_hsl_user
    ? hslServiceProviderTypes
    : walttiServiceProviderTypes

  const tabOptions = [{ label: t`All`, value: '' }, ...serviceProviderTypes]

  const handleTabChange = (_event: React.SyntheticEvent | null, newValue: string) => {
    setParams(prevState => {
      const newParams = { ...prevState, start_index: undefined }

      if (newValue) {
        newParams.type = newValue as ServiceProviderType
      } else {
        // Remove type if newValue is ''
        delete newParams.type
        searchParams.delete('type')
      }

      return newParams
    })
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleActivityDialogSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    activityData: ActivityData
  ) => {
    e.preventDefault()
    const serviceProviderData = {
      ...singleServiceProvider,
      ...activityData
    } as ServiceProvider

    dispatch(
      updateSingleServiceProvider({
        ...serviceProviderData,
        id: serviceProviderData.service_provider_id
      })
    )
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Device successfully updated.`)
        dispatch(fetchServiceProviders(params))
        dispatch(
          fetchSingleServiceProvider({
            id: selectedServiceProviderId as string
          })
        )
        handleActivityDialogClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error updating the device.`)
      })
  }

  const handleDelete = () => {
    if (!selectedServiceProviderId) return
    deleteServiceProvider(selectedServiceProviderId)
      .then(unwrapResult)
      .then(() => {
        fetchServiceProvidersWithParams(params)
        handleDrawerClose()
        SnackbarUtils.success(t`Service provider successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the service provider.`)
      })
  }

  const isLoading = loading === 'pending' || deleteLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Service providers`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new service provider`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <TabContext value={tabFromParams}>
            <TabList
              onChange={handleTabChange}
              aria-label={t`Product group tab list`}
              variant="scrollable"
            >
              {tabOptions?.map(({ label, value }) => (
                <Tab key={value} label={label} value={value} />
              ))}
            </TabList>
            <Box pt={1}>
              <WithLoader loading={isLoading} rows={1} height={33}>
                <ServiceProviderList
                  rows={serviceProviders}
                  isLoading={isLoading}
                  params={params}
                  handleRequestSort={handleRequestSort}
                  handleChangePage={handleChangePage}
                  count={total_results}
                  page={
                    params.start_index
                      ? Number.parseInt(params.start_index, 10) /
                        ROWS_PER_PAGE_DEFAULT
                      : 0
                  }
                  onRowClick={handleRowClick}
                  selectedServiceProviderId={selectedServiceProviderId}
                />
              </WithLoader>
            </Box>
          </TabContext>
          <ServiceProviderDrawer
            open={isDrawerOpen}
            handleClose={handleDrawerClose}
            handleServiceProviderDelete={handleDelete}
            handleActivityDialogOpen={handleActivityDialogOpen}
          />
        </Box>
      </Box>
      {singleServiceProvider && (
        <SPMActivityDialog
          open={activityDialogOpen}
          handleClose={handleActivityDialogClose}
          originalActivityEndDate={singleServiceProvider.activity_end_date?.toString()}
          isActive={singleServiceProvider.active}
          onSubmit={handleActivityDialogSubmit}
          spmObjectName={t`service provider`}
          isSubmitLoading={isSubmitLoading}
        />
      )}
    </Box>
  )
}
